import axios from "axios";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
});

const STORAGE_KEY = "mm_session";

export const authStore = {
    get() {
        try {
            return localStorage.getItem(STORAGE_KEY);
        } catch {
            return null;
        }
    },
    set(token) {
        try {
            if (token) localStorage.setItem(STORAGE_KEY, token);
            else localStorage.removeItem(STORAGE_KEY);
        } catch {
            // ignore
        }
    },
};

// Attach Bearer on every request (seam for Google OAuth swap later).
api.interceptors.request.use((config) => {
    const t = authStore.get();
    if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

// Clear token on 401 so the UI can reroute.
api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err?.response?.status === 401) {
            const t = authStore.get();
            if (t) authStore.set(null);
        }
        return Promise.reject(err);
    },
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

const AuthCtx = createContext({
    user: null,
    loading: true,
    requestMagicLink: async () => {},
    requestPasswordReset: async () => {},
    verifyToken: async () => {},
    loginWithPassword: async () => {},
    changePassword: async () => {},
    resetPassword: async () => {},
    signOut: async () => {},
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            // Playbook race fix: skip /auth/me when returning from the Google
            // OAuth callback — AuthGoogleCallback handles the handoff itself
            // and would race against this request.
            try {
                if (typeof window !== "undefined" && window.location?.hash?.includes("session_id=")) {
                    if (alive) setLoading(false);
                    return;
                }
            } catch { /* noop */ }
            if (!authStore.get()) {
                if (alive) setLoading(false);
                return;
            }
            try {
                const res = await api.get("/auth/me");
                if (alive) setUser(res.data);
            } catch {
                if (alive) {
                    authStore.set(null);
                    setUser(null);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    async function requestMagicLink(email) {
        const res = await api.post("/auth/request-magic-link", { email });
        return res.data; // { ok, message, dev_link? }
    }

    async function requestPasswordReset(email) {
        const res = await api.post("/auth/request-password-reset", { email });
        return res.data; // { ok, message, dev_link? }
    }

    async function loginWithPassword(email, password) {
        const res = await api.post("/auth/login", { email, password });
        authStore.set(res.data.session_token);
        setUser(res.data.user);
        return res.data.user;
    }

    async function changePassword(current_password, new_password) {
        const res = await api.post("/auth/change-password", {
            current_password, new_password,
        });
        if (res.data?.session_token) authStore.set(res.data.session_token);
        return res.data;
    }

    async function resetPassword(token, new_password) {
        const res = await api.post("/auth/reset-password", { token, new_password });
        authStore.set(res.data.session_token);
        setUser(res.data.user);
        return res.data.user;
    }

    async function verifyToken(token) {
        const res = await api.post("/auth/verify", { token });
        authStore.set(res.data.session_token);
        setUser(res.data.user);
        return res.data.user;
    }

    /**
     * Phase 12 — kick off Emergent-managed Google sign-in.
     * Calls backend /auth/google/start to build the redirect URL, then
     * navigates the browser to Google via Emergent. The `redirectTo` and
     * `chatSessionId` are preserved through the round-trip so the callback
     * can land the user back on the right page and reassign their
     * anonymous chat session, respectively.
     *
     * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
     * THIS BREAKS THE AUTH. The backend builds the exact redirect from the
     * `origin` we pass here (window.location.origin).
     */
    async function signInWithGoogle({ redirectTo = "/jarvis", chatSessionId = null } = {}) {
        const origin = window.location.origin;
        const params = new URLSearchParams({ redirect_to: redirectTo, origin });
        if (chatSessionId) params.set("chat_session_id", chatSessionId);
        const res = await api.get(`/auth/google/start?${params.toString()}`);
        if (!res?.data?.url) throw new Error("Could not start Google sign-in.");
        window.location.href = res.data.url;
    }

    async function signOut() {
        try {
            await api.post("/auth/logout");
        } catch {
            // stateless on server; ignore
        }
        authStore.set(null);
        setUser(null);
    }

    function setSession(token, userObj) {
        // Used by invite-accept and other token-based flows that already have
        // both the session JWT and the user payload in hand.
        if (token) authStore.set(token);
        if (userObj) setUser(userObj);
    }

    const value = useMemo(
        () => ({
            user, loading,
            requestMagicLink, requestPasswordReset, verifyToken,
            loginWithPassword, changePassword, resetPassword,
            signInWithGoogle,
            signOut, setSession,
        }),
        [user, loading],
    );
    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
